import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Spin,
  Select,
  Modal,
  message,
} from "antd";
import API, {
  GAMING_HIS,
  GAMING_HIS_EXCEL,
  GAMING_PREDICT_MATCH_DETAILS,
} from "../../network/api";
import moment from "moment";
import "moment-timezone";
import { TEXT_DEF } from "../../constant/text";
import {
  buildUsernameDataFilter,
  consumeUsernameFilterForHistory,
} from "../../utils/historyUsernameFilter";
import { VIP_TIER_KEYS } from "../../constants/vipTiers";
import {
  formatDateDashTimeDisplay,
  formatMatchDateAndKickoff,
  formatMomentDateDashTime,
} from "../../utils/formatDateDashTime";
import ListPagination, {
  LIST_PAGE_SIZE_OPTIONS,
  LIST_DEFAULT_PAGE_SIZE,
  buildPageQuery,
  createPageChangeHandler,
  parseTotalFromPayload,
} from "../../components/ListPagination";

/** Lọc kết quả dự đoán — query `predictResult`: WIN | LOSE | PENDING | ALL */
const PREDICT_RESULT_FILTER_OPTIONS = [
  { value: "ALL", label: "Tất cả" },
  { value: "WIN", label: "Đúng" },
  { value: "LOSE", label: "Sai" },
  { value: "PENDING", label: "Đang chờ" },
];

/** @typedef {Record<string, unknown>} HistoryRowRaw */

/** @param {HistoryRowRaw} item */
function pickTeamName(item) {
  const title =
    item?.matchTitle ??
    item?.matchName ??
    item?.pairing ??
    item?.teamPair ??
    item?.versus;
  if (typeof title === "string" && title.trim()) return title.trim();
  const h =
    item?.homeTeamName ??
    item?.homeTeam ??
    item?.teamHome ??
    item?.teamA;
  const a =
    item?.awayTeamName ??
    item?.awayTeam ??
    item?.teamAway ??
    item?.teamB;
  if (h || a)
    return [h ?? "?", a ?? "?"].join(" vs ");
  if (typeof item?.teamName === "string" && item.teamName.trim())
    return item.teamName.trim();
  return "—";
}

/** @param {HistoryRowRaw} item */
function pickPredictionFraction(item) {
  const flat = item?.predictionRatio ?? item?.predictRatio;
  if (typeof flat === "string" && flat.includes("/")) return flat;

  const used =
    item?.usedPredictionCount ??
    item?.predictionCount ??
    item?.guessCount ??
    item?.predictionsUsed ??
    item?.numPredictions ??
    item?.usedTurns ??
    item?.currentPredictionCount ??
    item?.predictionUsed;

  const max =
    item?.maxPredictionPerMatch ??
    item?.maxPredictionsPerMatch ??
    item?.predictionLimit ??
    item?.limitPredictPerMatch ??
    item?.totalPredictionSlots ??
    item?.totalSlots ??
    item?.allowPredictionCount ??
    item?.matchPredictionLimit ??
    item?.expectedPredictionCount ??
    item?.predictionTurnMax;

  if (used !== undefined && max !== undefined)
    return `${Number(used)}/${Number(max)}`;
  return "—";
}

/** Số lần dự đoán — BE: `predictionsCount` (ưu tiên), sau đó dạng x/y. */
function pickPredictionsCountDisplay(item) {
  const pc = item?.predictionsCount;
  if (pc !== undefined && pc !== null && String(pc).trim() !== "")
    return String(pc).trim();
  return pickPredictionFraction(item);
}

/** @param {HistoryRowRaw} item */
function pickMatchMoment(item) {
  const raw =
    item?.matchKickoff ??
    item?.kickoff ??
    item?.kickOffAt ??
    item?.kickoffAt ??
    item?.scheduledAt ??
    item?.scheduledTime ??
    item?.fixtureTime ??
    item?.fightTime ??
    item?.playedAt ??
    item?.battleTime;
  if (!raw && item?.kickoffMilliseconds != null)
    return moment(Number(item.kickoffMilliseconds));
  if (!raw && item?.scheduledTimestamp != null)
    return moment(Number(item.scheduledTimestamp));
  if (!raw) return null;
  return moment(raw);
}

/** Thời gian thi đấu — `matchTime` / `matchDate` + `kickoff` (vd. "13/06 - 08:00"). */
/** @param {HistoryRowRaw} item */
function pickMatchTimeDisplay(item) {
  const mt = item?.matchTime;
  if (typeof mt === "string" && mt.trim()) {
    return formatDateDashTimeDisplay(mt.trim());
  }
  const fromParts = formatMatchDateAndKickoff(
    typeof item?.matchDate === "string" ? item.matchDate : "",
    item?.kickoff != null ? String(item.kickoff) : ""
  );
  if (fromParts !== "—") return fromParts;
  return formatMomentDateDashTime(pickMatchMoment(item));
}

/** Thời gian dự đoán — BE: `lastPredictionAt` (ISO/timestamp/chuỗi) → `DD/MM - HH:mm`. */
/** @param {HistoryRowRaw} item */
function pickLastPredictionDisplay(item) {
  const raw = item?.lastPredictionAt;
  if (raw == null || String(raw).trim() === "") return "—";
  if (typeof raw === "string") {
    if (/\d{1,2}\/\d{1,2}/.test(raw) && /\d{1,2}:\d{2}/.test(raw)) {
      return formatDateDashTimeDisplay(raw.trim());
    }
  }
  const m = moment(raw);
  if (m.isValid()) return formatMomentDateDashTime(m);
  return typeof raw === "string" ? formatDateDashTimeDisplay(raw.trim()) : "—";
}

/**
 * Hiển thị «Kết quả» đúng theo BE: ưu tiên chuỗi `status`, không ép enum sang Đúng/Sai.
 * @param {HistoryRowRaw} item
 */
function pickResultDisplayText(item) {
  const candidates = [
    item?.status,
    item?.ketQua,
    item?.predictionResultLabel,
    item?.predictionResultText,
    item?.resultLabel,
    item?.result,
    item?.predictionResult,
    item?.guessResult,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return "—";
}

/** Điểm nhận thưởng — BE: `rewardScore`. */
/** @param {HistoryRowRaw} item */
function pickRewardScoreDisplay(item) {
  const raw = item?.rewardScore;
  if (raw === undefined || raw === null || String(raw).trim() === "") return "—";
  const n = Number(raw);
  if (!Number.isNaN(n)) return String(n);
  return String(raw).trim();
}

/** Trạng thái nhận thưởng — BE: `isRewardProcessed` (0 = chưa, 1 = đã). */
/** @param {HistoryRowRaw} item */
function pickRewardProcessedDisplay(item) {
  const raw = item?.isRewardProcessed;
  if (raw === undefined || raw === null || String(raw).trim() === "") return "—";
  const n = Number(raw);
  if (n === 1 || raw === true || raw === "true") return "Đã nhận";
  if (n === 0 || raw === false || raw === "false") return "Chưa nhận";
  return String(raw).trim();
}

/** Danh sách lượt dự đoán chi tiết trên từng trận (BE có thể đặt tên khác). */
function pickDetailItemsArray(parent) {
  const keys = [
    "predictions",
    "predictionHistories",
    "predictionDetails",
    "predictionList",
    "guessDetails",
    "guessList",
    "userPredictions",
    "predictMatchDetails",
    "detailList",
    "details",
    "items",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const v = parent?.[keys[i]];
    if (Array.isArray(v) && v.length) return v;
  }
  return null;
}

/** Thời gian user nhập dự đoán — BE: `predictedAt` + chuỗi literal, format `DD/MM - HH:mm`. */
/** @param {HistoryRowRaw} item */
function pickPredictionMomentDisplay(item) {
  const literal =
    item?.predictionTime ??
    item?.predictTime ??
    item?.guessTime ??
    item?.predictionTimeFormatted;
  if (typeof literal === "string" && literal.trim()) {
    return formatDateDashTimeDisplay(literal.trim());
  }

  const predictedRaw = item?.predictedAt;
  if (
    predictedRaw !== undefined &&
    predictedRaw !== null &&
    String(predictedRaw).trim() !== ""
  ) {
    if (typeof predictedRaw === "string") {
      return formatDateDashTimeDisplay(predictedRaw.trim());
    }
    const mp = moment(predictedRaw);
    if (mp.isValid()) return formatMomentDateDashTime(mp);
  }

  const m = moment(
    item?.submittedAt ??
      item?.predictionAt ??
      item?.guessAt ??
      item?.createdAt
  );
  if (m.isValid()) return formatMomentDateDashTime(m);
  return "—";
}

/** Tỉ số dự đoán hiển thị "x - y". */
/** @param {HistoryRowRaw} item */
function pickPredictedScoreLine(item) {
  const pre =
    item?.predictedScore ?? item?.scoreLine ?? item?.predictScore ?? item?.tiSo;
  if (typeof pre === "string" && pre.trim()) {
    return pre.trim().replace(/\s*-\s*/g, " - ");
  }
  const h =
    item?.homePredictedScore ??
    item?.predictHomeScore ??
    item?.homePrediction ??
    item?.predictedHome ??
    item?.predictionHome ??
    item?.homeScoreGuess;
  const a =
    item?.awayPredictedScore ??
    item?.predictAwayScore ??
    item?.awayPrediction ??
    item?.predictedAway ??
    item?.predictionAway ??
    item?.awayScoreGuess;
  if (h != null && a != null && h !== "" && a !== "") {
    return `${h} - ${a}`;
  }
  return "—";
}

/**
 * Modal chi tiết: «Số lần dự đoán» — BE `predictionOrder` (/ max nếu có).
 * @param {HistoryRowRaw} child
 * @param {HistoryRowRaw} parent
 * @param {number} indexZero
 */
function pickDetailAttemptFraction(child, parent, indexZero) {
  const slash =
    typeof child?.predictionRatio === "string"
      ? child.predictionRatio
      : typeof child?.predictRatio === "string"
        ? child.predictRatio
        : typeof child?.predictionsCount === "string" &&
            child.predictionsCount.includes("/")
          ? child.predictionsCount
          : typeof child?.attemptLabel === "string"
            ? child.attemptLabel
            : null;
  if (slash && slash.includes("/")) return slash.trim();

  /** @returns {unknown} */
  const pickMax = () =>
    child?.maxPredictionPerMatch ??
    child?.maxPredictions ??
    child?.maxPredictionsPerMatch ??
    parent?.maxPredictionsPerMatch ??
    parent?.maxPredictionPerMatch ??
    parent?.predictionLimit ??
    parent?.predictionTurnMax ??
    parent?.limitPredictPerMatch;

  const po = child?.predictionOrder;
  if (po !== undefined && po !== null && String(po).trim() !== "") {
    const nOrder = Number(po);
    const max = pickMax();
    const nMax = max !== undefined && max !== null ? Number(max) : NaN;
    if (!Number.isNaN(nMax) && nMax > 0 && !Number.isNaN(nOrder))
      return `${nOrder}/${nMax}`;
    return String(po).trim();
  }

  const raw =
    typeof child?.predictionsCount === "number" ||
    typeof child?.predictionsCount === "string"
      ? child.predictionsCount
      : undefined;
  if (typeof raw === "number" || (typeof raw === "string" && raw.includes("/"))) {
    if (typeof raw === "string" && raw.includes("/")) return raw.trim();
    if (typeof raw === "number") {
      const max = pickMax();
      const nMax = max !== undefined ? Number(max) : NaN;
      if (!Number.isNaN(nMax) && nMax > 0) return `${Number(raw)}/${nMax}`;
      return String(raw);
    }
  }

  const predictionAttemptNum = child?.predictionAttempt;
  if (
    predictionAttemptNum !== undefined &&
    predictionAttemptNum !== null &&
    String(predictionAttemptNum).trim() !== ""
  )
    return String(predictionAttemptNum).trim();

  const idx =
    child?.attemptNumber ??
    child?.order ??
    child?.attemptIndex ??
    child?.predictionIndex ??
    child?.sequence ??
    child?.index;

  const orderNum =
    idx != null && String(idx).trim() !== ""
      ? Number(idx)
      : indexZero + 1;

  const max = pickMax();
  if (
    max != null &&
    !Number.isNaN(Number(max)) &&
    Number(max) > 0
  ) {
    return `${Number.isNaN(orderNum) ? indexZero + 1 : orderNum}/${Number(max)}`;
  }

  const pc =
    typeof child?.predictionsCount === "number" ||
    typeof child?.predictionsCount === "string"
      ? String(child.predictionsCount)
      : null;
  return pc ?? "—";
}

function pickTeamParentFallback(child, parent) {
  const t = pickTeamName(child);
  if (t !== "—") return t;
  return pickTeamName(parent);
}

/**
 * Một dòng bảng modal chi tiết.
 * @param {HistoryRowRaw} child
 * @param {HistoryRowRaw} parent
 * @param {number} indexZero
 */
function mapPredictionDetailLine(child, parent, indexZero) {
  return {
    key: String(child?.id ?? child?.predictionId ?? `idx-${indexZero}`),
    teams: pickTeamParentFallback(child, parent),
    predictionTime: pickPredictionMomentDisplay(child),
    attemptFrac: pickDetailAttemptFraction(child, parent, indexZero),
    scoreLine: pickPredictedScoreLine(child),
    resultDisplay: pickResultDisplayText(child),
  };
}

/** `matchId` trên query predict-match-details (Swagger: number). */
function pickHistoryMatchId(item) {
  const raw =
    item?.matchId ??
    item?.aseanCupMatchId ??
    item?.worldCupMatchId ??
    item?.wcMatchId ??
    item?.fixtureId ??
    item?.scheduleId ??
    item?.gameMatchId ??
    item?.predictMatchId ??
    item?.match?.id ??
    item?.fixture?.id;
  if (raw === undefined || raw === null || String(raw).trim() === "")
    return null;
  const n = Number(raw);
  if (!Number.isNaN(n)) return n;
  return raw;
}

/**
 * Đảo payload GET predict-match-details → mảng bản ghi lượt dự đoán.
 * @param {unknown} body — thường `resp.data` hoặc `resp.data.data`
 */
function pickDetailApiList(body) {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return null;
  const o = /** @type {HistoryRowRaw} */ (body);
  const nested = pickDetailItemsArray(o);
  if (nested) return nested;
  const data = o.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.data))
    return data.data;
  if (Array.isArray(o.records)) return o.records;
  if (Array.isArray(o.results)) return o.results;
  return null;
}

/**
 * Chuẩn bị dòng cho modal «Chi tiết dự đoán» (offline từ bản ghi lịch sử).
 * @param {HistoryRowRaw} parent — bản ghi dòng của bảng lịch sử
 */
function buildPredictionDetailRows(parent) {
  const rows = pickDetailItemsArray(parent);
  if (rows && rows.length) {
    return rows.map((/** @type {HistoryRowRaw} */ child, i) =>
      mapPredictionDetailLine(child, parent, i)
    );
  }
  return [
    {
      key: "single",
      teams: pickTeamName(parent),
      predictionTime: pickPredictionMomentDisplay(parent),
      attemptFrac: pickPredictionsCountDisplay(parent),
      scoreLine: pickPredictedScoreLine(parent),
      resultDisplay: pickResultDisplayText(parent),
    },
  ];
}

const DETAIL_TABLE_COLUMNS = [
  {
    title: "Đội thi đấu",
    dataIndex: "teams",
    key: "teams",
  },
  {
    title: "Thời gian dự đoán",
    dataIndex: "predictionTime",
    key: "predictionTime",
    width: 150,
    align: "center",
  },
  {
    title: "Số lần dự đoán",
    dataIndex: "attemptFrac",
    key: "attemptFrac",
    width: 140,
    align: "center",
  },
  {
    title: "Tỉ số",
    dataIndex: "scoreLine",
    key: "scoreLine",
    width: 110,
    align: "center",
  },
  {
    title: "Kết quả",
    dataIndex: "resultDisplay",
    key: "resultDisplay",
    width: 120,
    align: "center",
    ellipsis: true,
    render: (_, row) => row.resultDisplay ?? "—",
  },
];

/** @param {HistoryRowRaw} item */
function mapHistoryRow(item, indexZero, pageZero, pageSize) {
  const kickoffForKey = pickMatchMoment(item);
  const idPart =
    item?.id ??
    item?.predictionId ??
    item?.guessId ??
    item?.historyId;
  const key =
    idPart ??
    `${String(item?.username ?? "")}_${String(
      typeof item?.matchTime === "string"
        ? item.matchTime
        : kickoffForKey?.valueOf?.() ?? item?.createdAt ?? indexZero
    )}`;

  return {
    key: `${pageZero}-${indexZero}-${String(key)}`,
    username: item?.username ?? "—",
    vipLevel:
      item?.vipLevel ??
      item?.vip ??
      item?.vipTier ??
      "—",
    teamName: pickTeamName(item),
    predictionFraction: pickPredictionsCountDisplay(item),
    matchTimeDisplay: pickMatchTimeDisplay(item),
    lastPredictionDisplay: pickLastPredictionDisplay(item),
    resultDisplay: pickResultDisplayText(item),
    rewardScore: pickRewardScoreDisplay(item),
    rewardProcessedDisplay: pickRewardProcessedDisplay(item),
    raw: item,
  };
}

const GamingHistoryList = () => {
  const location = useLocation();
  const initialUsername = consumeUsernameFilterForHistory(location);
  const [form] = Form.useForm();
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState(() =>
    buildUsernameDataFilter(initialUsername)
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const listFetchSeqRef = useRef(0);

  const locale = { emptyText: TEXT_DEF.text0035 };

  useEffect(() => {
    if (initialUsername) {
      form.setFieldValue("usernameFilter", initialUsername);
    }
  }, [form, initialUsername]);

  useEffect(() => {
    getListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dataFilter]);

  const buildQuery = () => {
    const qs = [buildPageQuery(page, pageSize).query];
    if (dataFilter && dataFilter.trim() !== "") qs.push(dataFilter);
    return qs.join("&");
  };

  const getListData = async () => {
    const fetchSeq = ++listFetchSeqRef.current;
    setLoading(true);
    try {
      const resp = await API.get(`${GAMING_HIS}?${buildQuery()}`);
      if (fetchSeq !== listFetchSeqRef.current) return;

      const payload = resp?.data?.data;
      const list = payload?.data ?? [];
      const pageZero = page - 1;
      const mapped = list.map((item, i) =>
        mapHistoryRow(item, i, pageZero, pageSize)
      );

      setDataList(mapped);
      setTotalRecord(parseTotalFromPayload(payload, mapped.length));
    } catch (e) {
      if (fetchSeq !== listFetchSeqRef.current) return;
      console.log(e);
    } finally {
      if (fetchSeq === listFetchSeqRef.current) setLoading(false);
    }
  };

  const onSearch = () => {
    /** @type {string[]} */
    const parts = [];

    const u = form.getFieldValue("usernameFilter");
    if (u) parts.push(`username=${String(u).trim().toLowerCase()}`);

    const vip = form.getFieldValue("vipFilter");
    if (vip !== undefined && vip !== null && vip !== "") {
      parts.push(`vipLevel=${vip}`);
    }

    const predictResult = form.getFieldValue("predictResultFilter");
    if (
      predictResult === "WIN" ||
      predictResult === "LOSE" ||
      predictResult === "PENDING" ||
      predictResult === "ALL"
    ) {
      parts.push(`predictResult=${predictResult}`);
    }

    if (dateFrom) parts.push(`fromDate=${dateFrom}`);
    if (dateTo) {
      parts.push(`toDate=${dateTo}`);
      if (!dateFrom) parts.push("fromDate=2025-07-15");
    }

    const qs = parts.join("&").replace(/^&+/, "");
    setPage(1);
    setDataFilter(qs !== "" ? qs : null);
  };

  const closeDetailModal = () => {
    setDetailOpen(false);
    setDetailRows([]);
    setDetailLoading(false);
  };

  /**
   * @param {{ username?: string, raw?: HistoryRowRaw }} row
   */
  const openDetail = async (row) => {
    const parent = row?.raw && typeof row.raw === "object" ? row.raw : {};
    const username = String(row?.username ?? parent?.username ?? "")
      .trim()
      .toLowerCase();
    const matchId = pickHistoryMatchId(parent);

    setDetailOpen(true);
    setDetailRows([]);
    setDetailLoading(true);

    const applyFallback = () => {
      setDetailRows(buildPredictionDetailRows(parent));
    };

    if (!username || matchId === null || matchId === "") {
      setDetailLoading(false);
      applyFallback();
      message.warning(
        "Không có username hoặc matchId trên dòng lịch sử — hiển thị dữ liệu gắn sẵn (nếu có)."
      );
      return;
    }

    const qs = `username=${encodeURIComponent(username)}&matchId=${encodeURIComponent(String(matchId))}`;

    try {
      const resp = await API.get(`${GAMING_PREDICT_MATCH_DETAILS}?${qs}`);
      const root = resp?.data ?? {};
      const payload = root?.data !== undefined ? root.data : root;
      const list = pickDetailApiList(payload);

      if (list && list.length) {
        setDetailRows(
          list.map((/** @type {HistoryRowRaw} */ item, i) =>
            mapPredictionDetailLine(item, parent, i)
          )
        );
      } else {
        applyFallback();
      }
    } catch (e) {
      console.log(e);
      const data = e?.response?.data;
      const msg =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        (typeof e?.message === "string" ? e.message : null);
      message.error(msg || "Không tải được chi tiết dự đoán.");
      applyFallback();
    } finally {
      setDetailLoading(false);
    }
  };

  const vipOptions = [
    { value: "", label: "Tất cả" },
    ...VIP_TIER_KEYS.map((tier) => ({ value: tier, label: tier })),
  ];

  const dataColums = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      align: "center",
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    { title: TEXT_DEF.text0002, dataIndex: "username", key: "username" },
    {
      title: "Cấp VIP",
      dataIndex: "vipLevel",
      key: "vipLevel",
      width: 100,
      align: "center",
    },
    {
      title: "Tên đội",
      dataIndex: "teamName",
      key: "teamName",
    },
    {
      title: "Số lần dự đoán",
      dataIndex: "predictionFraction",
      key: "predictionFraction",
      width: 140,
      align: "center",
    },
    {
      title: "Thời gian thi đấu",
      dataIndex: "matchTimeDisplay",
      key: "matchTimeDisplay",
      width: 150,
      align: "center",
    },
    {
      title: "Thời gian dự đoán",
      dataIndex: "lastPredictionDisplay",
      key: "lastPredictionDisplay",
      width: 160,
      align: "center",
    },
    {
      title: "Kết quả",
      dataIndex: "resultDisplay",
      key: "resultDisplay",
      width: 110,
      align: "center",
      ellipsis: true,
      render: (v, row) => row.resultDisplay ?? "—",
    },
    {
      title: "Điểm nhận thưởng",
      dataIndex: "rewardScore",
      key: "rewardScore",
      width: 130,
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Đã nhận thưởng",
      dataIndex: "rewardProcessedDisplay",
      key: "rewardProcessedDisplay",
      width: 130,
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Hành động",
      key: "actions",
      width: 130,
      align: "center",
      render: (_, row) => (
        <Button type="primary" size="small" onClick={() => openDetail(row)}>
          Chi tiết
        </Button>
      ),
    },
  ];

  const onChangeDate = (e, type) => {
    if (e) {
      const d = moment(e.$d);
      if (type === "from") setDateFrom(d.startOf("day").format("YYYY-MM-DD"));
      else setDateTo(d.endOf("day").format("YYYY-MM-DD"));
    } else {
      if (type === "from") setDateFrom(null);
      else setDateTo(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const qs = dataFilter ? `?${dataFilter}` : "";
      const response = await API.post(
        `${GAMING_HIS_EXCEL}${qs}`,
        {},
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers?.["content-disposition"];
      let fileName = "lich_su_choi_game.xlsx";
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].replace(/"/g, "");
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderHistory = () => {
    return (
      <>
        <div className="col-12">
          <Form
            form={form}
            layout="inline"
            autoComplete="off"
            onFinish={onSearch}
            initialValues={{
              vipFilter: "",
              predictResultFilter: "ALL",
            }}
          >
            <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
              <div className="col-4">
                <Form.Item name="usernameFilter" label={TEXT_DEF.text0002}>
                  <Input style={{ width: "100%" }} allowClear />
                </Form.Item>
              </div>
              <div className="col-4">
                <Form.Item name="vipFilter" label="Cấp VIP">
                  <Select
                    allowClear={false}
                    options={vipOptions}
                    placeholder="Tất cả"
                    style={{ width: "100%", minHeight: 32 }}
                  />
                </Form.Item>
              </div>
              <div className="col-4">
                <Form.Item name="predictResultFilter" label="Trạng thái">
                  <Select
                    allowClear={false}
                    placeholder="Tất cả"
                    style={{ width: "100%", minHeight: 32 }}
                    options={PREDICT_RESULT_FILTER_OPTIONS}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
              <div className="col-5">
                <Form.Item name="dateFrom" label={TEXT_DEF.text0032}>
                  <DatePicker
                    placeholder=""
                    onChange={(e) => onChangeDate(e, "from")}
                    format={"DD-MM-YYYY"}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
              <div className="col-5">
                <Form.Item name="dateTo" label={TEXT_DEF.text0033}>
                  <DatePicker
                    placeholder=""
                    onChange={(e) => onChangeDate(e, "to")}
                    format={"DD-MM-YYYY"}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="payment-search">
              <Button htmlType="submit" type="primary">
                {TEXT_DEF.text0031}
              </Button>
              <Button
                style={{ marginLeft: 20 }}
                onClick={handleDownloadExcel}
                htmlType="button"
                type="primary"
              >
                {TEXT_DEF.text0050}
              </Button>
            </div>
          </Form>
        </div>

        <Spin spinning={loading} delay={500} size="large">
          <div style={{ display: "flex", width: "100%", marginTop: 10 }}>
            <Table
              key={`gamingHistory-p${page}-s${pageSize}`}
              locale={locale}
              columns={dataColums}
              dataSource={dataList}
              pagination={false}
              bordered
              style={{ width: "100%" }}
            />
          </div>
          <ListPagination
            current={page}
            total={totalRecord}
            pageSize={pageSize}
            onChange={createPageChangeHandler(
              page,
              pageSize,
              setPage,
              setPageSize
            )}
            pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
            unit="bản ghi"
          />
        </Spin>

        <Modal
          title="Chi tiết dự đoán"
          open={detailOpen}
          centered
          onCancel={closeDetailModal}
          footer={[
            <Button key="close" type="primary" onClick={closeDetailModal}>
              Đóng
            </Button>,
          ]}
          width={900}
        >
          <Spin spinning={detailLoading}>
            <Table
              locale={locale}
              columns={DETAIL_TABLE_COLUMNS}
              dataSource={detailRows}
              pagination={false}
              bordered
              size="small"
              scroll={{ y: 420, x: 720 }}
            />
          </Spin>
        </Modal>
      </>
    );
  };

  return <div className="col-12">{renderHistory()}</div>;
};

export default GamingHistoryList;
